import { useDispatch } from 'react-redux';

import type { ProductTerm } from '@sravni/react-ugc/lib/ProductTerms';
import { useIsMobile } from '@sravni/react-utils';
import { hasItem, normalizeTabs } from '@sravni/ugc/lib/specialOffers/helpers';
import { BUSINESS_OFFERS, COMMON_OFFERS, OTHER_OFFERS } from '@sravni/ugc/lib/specialOffers/lists';
import type { Tab, VITRINS_OFFERS_TYPE } from '@sravni/ugc/lib/specialOffers/types';

import type { OfferTab } from '../helpers/getTabsInfo';
import { getTabsInfo } from '../helpers/getTabsInfo';
import { getOfferLinks } from '../helpers/offerLinks/getOfferLinks';
import type { EventPayload } from '../helpers/sendMessageToParentWindow';
import type { EnrichedOffer } from '../interfaces/offers';
import type { IShortOrganization } from '../interfaces/organizations';
import { useAnalyticsParams, useIsAnalyticsParamsLoaded } from '../store/hooks/offers';
import { addPixel } from '../store/reducers/offers';
import { WIDGET_TYPE } from '../types/widgetType';

const EXCEPT_NAMES = [
  {
    name: 'Московский Кредитный Банк',
    shortName: 'МКБ',
  },
  {
    name: 'Азиатско-Тихоокеанский Банк',
    shortName: 'АТБ',
  },
];

const OFFER_TYPES_WHOSE_DONT_LIKE_TO_SHOW_NO_LABEL = [
  BUSINESS_OFFERS.biznesLeasing,
  BUSINESS_OFFERS.biznesEkvajring,
  BUSINESS_OFFERS.biznesAccounting,
  BUSINESS_OFFERS.biznesRegistration,
  BUSINESS_OFFERS.biznesRko,
  BUSINESS_OFFERS.biznesKredity,
];

const getConditions = (product: EnrichedOffer, tabInfo: OfferTab, widgetType?: WIDGET_TYPE): ProductTerm[] => {
  const conditionsFunction =
    widgetType === WIDGET_TYPE.vertical ? tabInfo.getVerticalWidgetConditions : tabInfo.getConditions;

  const initialConditions = conditionsFunction(product) ?? [];

  const labelKeys = initialConditions.map((condition) => condition.labelKey).filter(Boolean);

  const conditions = initialConditions
    .map((condition) => {
      const { labelKey, key, value } = condition;
      if (labelKeys.includes(key)) {
        return null;
      }

      if (hasItem(normalizeTabs(OFFER_TYPES_WHOSE_DONT_LIKE_TO_SHOW_NO_LABEL), tabInfo.key) && value === 'Нет') {
        return null;
      }

      if (labelKey) {
        const conditionAsLabel = initialConditions.find(({ key }) => key === labelKey);

        return {
          ...condition,
          ...(conditionAsLabel && { description: (conditionAsLabel.SOValue as string) ?? conditionAsLabel.value }),
        };
      }

      return condition;
    })
    .filter(Boolean);

  return conditions;
};

const getAdvertisingAction = ({
  tab,
  advertisingInfo,
}: {
  tab: Tab<VITRINS_OFFERS_TYPE>;
  advertisingInfo: Record<string, any>;
}) => {
  if (tab.type === BUSINESS_OFFERS.biznesLeasing) {
    return 'Блок|Переход на витрину';
  }

  return advertisingInfo?.monetization?.kind === 'referral' ? 'Платный клик' : 'Переход к оформлению заявки';
};

const getAdvertisingLabel = ({
  offerName,
  offerId,
  organizationName,
  isVerticalWidget,
  tab,
}: {
  offerName: string;
  offerId: string;
  organizationName: string;
  isVerticalWidget: boolean;
  tab: Tab<VITRINS_OFFERS_TYPE>;
}) => {
  if (isVerticalWidget && tab.type === OTHER_OFFERS.bukmekery) {
    return `${organizationName}|${offerName}|${offerId}`;
  }

  if (tab.type === BUSINESS_OFFERS.biznesLeasing) {
    const tabInfo = getTabsInfo(tab);
    return `${organizationName}|${offerName}|${tabInfo.generalLink}`;
  }

  return `${organizationName}|${offerName}`;
};

// eslint-disable-next-line max-statements,complexity
export const useOfferData = ({
  tab,
  item,
  isPopup,
  widgetType,
}: {
  tab: Tab<VITRINS_OFFERS_TYPE>;
  item: EnrichedOffer;
  isPopup: boolean;
  widgetType?: WIDGET_TYPE;
}) => {
  const isMobile = useIsMobile();
  const analyticsParams = useAnalyticsParams();
  const dispatch = useDispatch();
  const isAnalyticsParamsLoaded = useIsAnalyticsParamsLoaded();
  const isVerticalWidget = widgetType === WIDGET_TYPE.vertical;

  const organization = item.organizationInfo ?? ({} as IShortOrganization);

  const ratingValue = organization?.rating ?? item.ratingInfo?.complexCalculatedRatingValue;
  const reviewsCount = organization?.reviewsCount ?? item.ratingInfo?.participantsCount;
  // const { rating: ratingValue, reviewsCount } = item.productRating ?? {};

  const organizationName = organization?.name;

  const isCard = tab.type === COMMON_OFFERS.creditCards || tab.type === COMMON_OFFERS.debitCards;

  const offerTitle = isCard ? item.name : `«${item.name}»`;

  let logoSrc = '';
  if (isCard) {
    logoSrc = item.productLogo;
  } else if (tab.type === COMMON_OFFERS.loan) {
    logoSrc = organization?.logotypes?.android;
  } else {
    logoSrc = organization?.logotypes?.square;
  }

  const exceptName = EXCEPT_NAMES.find((elem) => elem.name === organizationName);
  const bankName = exceptName ? exceptName.shortName : organizationName;

  const advertisingInfo = item.ad || item.advertising;

  const tabInfo = getTabsInfo(tab);
  const { advertisingCategory, buttonAction } = tabInfo;

  const conditions = getConditions(item, tabInfo, widgetType);

  const advertisingAction = getAdvertisingAction({ tab, advertisingInfo });
  const advertisingLabel = getAdvertisingLabel({
    organizationName,
    offerName: item.name,
    isVerticalWidget,
    tab,
    offerId: item._id,
  });
  const advertisingValue = advertisingInfo?.cost;

  // let noticeAboutAdvertisement = `Реклама. ${organization?.name.full ?? organization?.name.short}`;
  let noticeAboutAdvertisement = null;

  if (tab.type === OTHER_OFFERS.bukmekery) {
    noticeAboutAdvertisement = null;
  }

  const tag = item.bonus;

  const clickBlockEvent = {
    eventCategory: advertisingCategory,
    eventAction: advertisingAction,
    eventLabel: advertisingLabel,
    eventValue: advertisingValue,
    event: 'mainEvent',
  };

  const viewBlockEvent = {
    eventCategory: advertisingCategory,
    eventAction: 'Показ',
    eventLabel: advertisingLabel,
    event: 'mainEvent',
  };

  const isLeasing = tab.type === BUSINESS_OFFERS.biznesLeasing;

  const offerTitleClickEvent: EventPayload = isLeasing
    ? {
        eventAction: 'Заголовок|Переход на витрину',
        eventCategory: tabInfo.advertisingCategory,
        eventLabel: tabInfo.generalLink,
        event: 'mainEvent',
      }
    : undefined;

  const buttonClickEvent: EventPayload = isLeasing
    ? {
        eventAction: 'Кнопка Подробнее|Переход на витрину',
        eventCategory: tabInfo.advertisingCategory,
        eventLabel: `${organizationName}|${item.name}|${tabInfo.generalLink}`,
        event: 'mainEvent',
      }
    : undefined;

  const utmParams = {
    ...analyticsParams.utmParams,
    aff_sub4: `${advertisingLabel}|${organization.id}|${item._id}`,
    aff_sub5: analyticsParams.testIdVariant
      ? `${advertisingCategory}|${analyticsParams.testIdVariant}`
      : advertisingCategory,
  };

  const { viewUrl, clickUrl } = getOfferLinks({
    item,
    tab,
    utmParams,
    organization,
    isPopup,
  });

  const sendViewingEventViaPixel = () => {
    if (viewUrl && isAnalyticsParamsLoaded) {
      dispatch(addPixel(viewUrl));
    }
  };
  const sendClickEventViaPixel = () => {
    if (clickUrl && isAnalyticsParamsLoaded) {
      dispatch(addPixel(clickUrl));
    }
  };

  let buttonLabel = '';

  if (isVerticalWidget) {
    if (item.advertising) {
      buttonLabel = isMobile ? item.advertising.buttonMobileText : item.advertising.buttonText;
    } else {
      switch (tab.type) {
        case COMMON_OFFERS.loan: {
          buttonLabel = `${buttonAction} в ${bankName}`;
          break;
        }
        case OTHER_OFFERS.bukmekery: {
          buttonLabel = 'Получить бонус';
          break;
        }
        default: {
          buttonLabel = 'Подробнее';
        }
      }
    }
  } else {
    switch (tab.type) {
      case COMMON_OFFERS.loan:
      case COMMON_OFFERS.mortgage: {
        buttonLabel = 'Перейти на сайт';
        break;
      }
      case COMMON_OFFERS.bankrotstvo: {
        buttonLabel = 'Узнать бесплатно';
        break;
      }
      default: {
        buttonLabel = 'Подробнее';
      }
    }
  }

  return {
    bankName,
    logoSrc,
    isCard,
    offerTitle,
    ratingValue,
    reviewsCount,
    conditions,
    partnerCard: organization?.partnerCard,
    organization,
    noticeAboutAdvertisement,
    tag,
    buttonLabel,
    clickBlockEvent,
    viewBlockEvent,
    offerTitleClickEvent,
    buttonClickEvent,
    clickUrl,
    sendViewingEventViaPixel,
    sendClickEventViaPixel,
    labelFromAdvertising: advertisingInfo?.labelText,
  };
};
